import { type ReactElement } from "react"
import { Link } from "react-router-dom"
import { HeroHeader } from "@/components/ui/hero-section-1"
import { AnimatedGroup } from "@/components/ui/animated-group"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Compass,
  Globe2,
  Handshake,
  Headphones,
  HeartHandshake,
  Sparkles,
  Target,
} from "lucide-react"

type Milestone = {
  year: string
  title: string
  description: string
}

const milestones: Milestone[] = [
  {
    year: "2019",
    title: "Braise founded",
    description: "Former hospitality operators and POS engineers partnered to design the orchestration stack they always wanted on the line.",
  },
  {
    year: "2020",
    title: "Automation engine launch",
    description: "Introduced real-time automations for expo pacing, curbside routing, and staff escalations during service pivots.",
  },
  {
    year: "2022",
    title: "Signal telemetry platform",
    description: "Built end-to-end observability-device health, guest sentiment, and crew adoption signals for every venue.",
  },
  {
    year: "2024",
    title: "Global expansion",
    description: "Braise orchestrates hospitality operations across North America, Europe, and APAC with localized playbooks.",
  },
]

const leadershipPrinciples = [
  {
    icon: HeartHandshake,
    title: "Hospitality first",
    description: "We measure success by guest delight and crew calm. Every feature is co-designed with operators on the floor.",
  },
  {
    icon: Target,
    title: "Obsess over signals",
    description: "Telemetry, data integrity, and clarity power every decision. If we can’t measure it, we can’t automate it.",
  },
  {
    icon: Compass,
    title: "Deliver clarity",
    description: "Braise surfaces the signal that matters and hides the noise-so operators make moves with confidence.",
  },
  {
    icon: Handshake,
    title: "Earn trust daily",
    description: "We partner long-term, embedding with your crew for openings, rushes, and retros. Our north star is your throughput.",
  },
]

const founderProfile = {
  name: "Shubh Dholakiya",
  headline: "Engineer · Youngest Innovator of Asia · Entrepreneur",
  background:
    "Shubh built Braise from the line out-engineering orchestration that keeps hospitality crews nimble while every signal stays actionable.",
  quote:
    "Hard work only becomes magnetic when creativity keeps it restless. That balance is how we turn everyday service into unforgettable hospitality.",
}

const globalFootprint = [
  { region: "North America", detail: "Stadiums, food halls, fleet operations, and fine dining concepts." },
  { region: "Europe", detail: "Multi-market operators orchestrating kiosks, QR, and loyalty signals." },
  { region: "APAC", detail: "High-volume venues with automation-led labor optimization." },
]

const AboutPageContent = (): ReactElement => (
  <div className="min-h-screen bg-inverse text-inverse-foreground selection:bg-emerald-100 selection:text-emerald-900">
    <HeroHeader />
    <main className="relative">
      <section className="relative isolate overflow-hidden pb-32 pt-36 md:pb-44 md:pt-44">
        <div className="absolute inset-0 -z-20 bg-radial bg-radial-[at_50%_-15%] from-inverse-highlight/24 via-transparent to-transparent" />
        <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_105%] from-inverse-highlight/16 via-transparent to-inverse-background/95" />
        <AnimatedGroup
          variants={{
            container: {
              visible: { transition: { staggerChildren: 0.1, delayChildren: 0.12 } },
            },
            item: {
              hidden: { opacity: 0, filter: "blur(14px)", y: 24 },
              visible: {
                opacity: 1,
                filter: "blur(0px)",
                y: 0,
                transition: { type: "spring", duration: 1.1, bounce: 0.25 },
              },
            },
          }}
          className="mx-auto flex max-w-5xl flex-col gap-6 px-6 text-center"
        >
          <Badge className="mx-auto w-fit rounded-full bg-inverse-surface/40 px-5 py-2 text-[0.65rem] uppercase tracking-[0.32em] text-inverse-muted-foreground">
            About Braise
          </Badge>
          <h1 className="text-balance text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
            Built by operators who wanted every hospitality move orchestrated
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-inverse-muted-foreground">
            Braise started in back-of-house break rooms, not boardrooms. Today we power orchestrated service for teams that expect every signal to count.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-2xl bg-white px-6 text-base text-black shadow-[0_30px_90px_rgba(255,255,255,0.35)]">
              <Link to="/pricing">Explore plans</Link>
            </Button>
            <Button asChild size="lg" variant="ghost" className="rounded-2xl px-6 text-base text-inverse-foreground hover:bg-inverse-surface/30">
              <Link to={{ pathname: "/", hash: "demo" }}>Start demo</Link>
            </Button>
          </div>
        </AnimatedGroup>
      </section>

      <section className="relative mt-24 pb-28 pt-12 md:mt-32 md:pt-16">
        <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_10%] from-rose-400/20 via-transparent to-transparent" />
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="rounded-full bg-inverse-surface/40 px-4 py-1 text-[0.65rem] uppercase tracking-[0.32em] text-inverse-muted-foreground">
              Principles
            </Badge>
            <h2 className="mt-6 text-3xl font-semibold sm:text-4xl">The operating system we wished we had</h2>
            <p className="mt-3 text-lg text-inverse-muted-foreground">
              Braise is opinionated on purpose. These principles guide every product iteration, rollout, and partnership.
            </p>
          </div>
          <AnimatedGroup
            variants={{
              container: {
                visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
              },
              item: {
                hidden: { opacity: 0, filter: "blur(10px)", y: 24 },
                visible: {
                  opacity: 1,
                  filter: "blur(0px)",
                  y: 0,
                  transition: { type: "spring", duration: 1, bounce: 0.25 },
                },
              },
            }}
            className="mt-12 grid gap-6 md:grid-cols-2"
          >
            {leadershipPrinciples.map(({ icon: Icon, title, description }) => (
              <div key={title} className="relative overflow-hidden rounded-3xl border border-inverse-border/45 bg-inverse-surface/90 p-7 shadow-inverse-strong">
                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-inverse-highlight/25 blur-3xl" />
                <div className="relative flex items-start gap-4">
                  <span className="flex size-12 items-center justify-center rounded-2xl bg-inverse-surface/40 text-inverse-foreground">
                    <Icon className="size-6" aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-inverse-foreground">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-inverse-muted-foreground">{description}</p>
                  </div>
                </div>
              </div>
            ))}
          </AnimatedGroup>
        </div>
      </section>

      <section className="relative mt-24 pb-28 pt-12 md:mt-32 md:pt-16">
        <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_0%] from-inverse-highlight/18 via-transparent to-transparent" />
        <div className="mx-auto max-w-6xl px-6">
          <AnimatedGroup
            variants={{
              container: {
                visible: { transition: { staggerChildren: 0.1, delayChildren: 0.12 } },
              },
              item: {
                hidden: { opacity: 0, filter: "blur(12px)", y: 24 },
                visible: {
                  opacity: 1,
                  filter: "blur(0px)",
                  y: 0,
                  transition: { type: "spring", duration: 1.05, bounce: 0.26 },
                },
              },
            }}
            className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr]"
          >
            <div className="space-y-4">
              <Badge className="rounded-full bg-inverse-surface/40 px-4 py-1 text-[0.65rem] uppercase tracking-[0.32em] text-inverse-muted-foreground">
                Milestones
              </Badge>
              <h2 className="text-3xl font-semibold sm:text-4xl">A signal-rich path from a single venue to global orchestration</h2>
              <p className="text-lg text-inverse-muted-foreground">
                Braise grows with the operators we serve. Each milestone reflects a new set of challenges we solved alongside the crew.
              </p>
            </div>
            <div className="rounded-[2rem] border border-inverse-border/45 bg-inverse-surface/80 p-8 shadow-inverse-strong">
              <h3 className="text-sm font-semibold uppercase tracking-[0.32em] text-inverse-muted-foreground/85">Global footprint</h3>
              <ul className="mt-6 space-y-3 text-sm text-inverse-muted-foreground">
                {globalFootprint.map(({ region, detail }) => (
                  <li key={region} className="flex items-start gap-3">
                    <Globe2 className="mt-1 size-4 text-inverse-muted-foreground/88" aria-hidden />
                    <div>
                      <span className="text-sm font-semibold text-inverse-foreground">{region}</span>
                      <p className="text-xs text-inverse-muted-foreground/90">{detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedGroup>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {milestones.map(({ year, title, description }) => (
              <div key={year} className="rounded-3xl border border-inverse-border/45 bg-inverse-surface/90 p-6 shadow-inverse-strong">
                <span className="text-xs font-semibold uppercase tracking-[0.32em] text-inverse-muted-foreground/80">{year}</span>
                <h3 className="mt-3 text-lg font-semibold text-inverse-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-inverse-muted-foreground">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative mt-24 pb-28 pt-12 md:mt-32 md:pt-16">
        <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_10%] from-inverse-highlight/20 via-transparent to-transparent" />
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="rounded-full bg-inverse-surface/40 px-4 py-1 text-[0.65rem] uppercase tracking-[0.32em] text-inverse-muted-foreground">
              Founder
            </Badge>
            <h2 className="mt-6 text-3xl font-semibold sm:text-4xl">Meet the founder guiding Braise forward</h2>
            <p className="mt-3 text-lg text-inverse-muted-foreground">
              Braise is led by a founder who blends engineering rigor with operator instincts so every crew stays orchestrated.
            </p>
          </div>
          <AnimatedGroup
            variants={{
              container: {
                visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
              },
              item: {
                hidden: { opacity: 0, filter: "blur(10px)", y: 24 },
                visible: {
                  opacity: 1,
                  filter: "blur(0px)",
                  y: 0,
                  transition: { type: "spring", duration: 1, bounce: 0.25 },
                },
              },
            }}
            className="mt-12 grid gap-6 md:grid-cols-[0.65fr_1fr]"
          >
            <div className="rounded-3xl border border-inverse-border/45 bg-inverse-surface/90 p-6 shadow-inverse-strong">
              <span className="text-xs font-semibold uppercase tracking-[0.32em] text-inverse-muted-foreground/80">Founder & Engineer</span>
              <h3 className="mt-2 text-lg font-semibold text-inverse-foreground">{founderProfile.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-inverse-muted-foreground">{founderProfile.headline}</p>
            </div>
            <div className="flex flex-col justify-center gap-6 rounded-3xl border border-inverse-border/45 bg-inverse-surface/70 p-6 shadow-[0_40px_120px_rgba(0,0,0,0.33)] md:p-8">
              <p className="text-base leading-relaxed text-inverse-muted-foreground/88">{founderProfile.background}</p>
              <blockquote className="text-left text-lg font-medium italic leading-relaxed text-inverse-foreground/80">
                &ldquo;{founderProfile.quote}&rdquo;
              </blockquote>
            </div>
          </AnimatedGroup>
        </div>
      </section>

      <section className="relative mt-24 pb-32 pt-12 md:mt-32 md:pt-16">
        <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_15%] from-emerald-500/20 via-transparent to-transparent" />
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-[2.5rem] border border-inverse-border/45 bg-inverse-surface/80 p-12 text-center shadow-inverse-strong backdrop-blur">
            <Badge className="rounded-full bg-inverse-surface/40 px-4 py-1 text-[0.65rem] uppercase tracking-[0.32em] text-inverse-muted-foreground">
              Partner with us
            </Badge>
            <h2 className="mt-6 text-3xl font-semibold sm:text-4xl">Let’s map your orchestration blueprint together</h2>
            <p className="mt-3 text-lg text-inverse-muted-foreground">
              Whether you’re opening your first concept or coordinating international franchises, Braise has an operator-led team ready to help.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="rounded-2xl bg-white px-6 text-base text-black shadow-[0_18px_52px_rgba(255,255,255,0.22)]">
                <Link to="/pricing">Choose your plan</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-2xl px-6 text-base text-inverse-foreground hover:bg-inverse-surface/30">
                <Link to="/stories">See operator stories</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-2xl px-6 text-base text-inverse-foreground hover:bg-inverse-surface/30">
                <Link to="/ai-features">Explore AI features</Link>
              </Button>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5 text-sm text-inverse-muted-foreground/85">
            <span className="inline-flex items-center gap-2 uppercase tracking-[0.28em]">
              <Sparkles className="size-4" aria-hidden />
              Operator-led onboarding
            </span>
            <span className="inline-flex items-center gap-2 uppercase tracking-[0.28em]">
              <Headphones className="size-4" aria-hidden />
              24/7 concierge support
            </span>
          </div>
        </div>
      </section>
    </main>
  </div>
)

const AboutPage = (): ReactElement => <AboutPageContent />

export default AboutPage
